import { useState } from 'react';
import { api } from '../../api/client.js';
import {
  Alert,
  Field,
  PageHeader,
  StatusPill,
  fehlertext,
  useLoad,
} from '../shared/ui.js';

interface ProductTypeOption {
  id: string;
  code: string;
  label: { de?: string };
  isActive: boolean;
}

interface MaterialOption {
  id: string;
  code: string;
  label: { de?: string };
  isActive: boolean;
}

interface SearchTag {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  isActive: boolean;
  showAsQuickFilter: boolean;
  quickFilterSortOrder: number;
  synonyms: string[];
  productTypes: Array<{ id: string; code: string; label: { de?: string } }>;
  materials: Array<{ id: string; code: string; label: { de?: string } }>;
}

interface SearchTagForm {
  key: string;
  displayName: string;
  description: string;
  isActive: boolean;
  showAsQuickFilter: boolean;
  quickFilterSortOrder: number;
  synonyms: string[];
  productTypeValueIds: string[];
  materialValueIds: string[];
}

/**
 * Such-Tags fuer die Produktsuche. Reine Sucherleichterung -- kein Rabattbezug,
 * keine technische Eignungsaussage. Nur Betreiber.
 */
export function SearchTagsPage(): React.JSX.Element {
  const tags = useLoad<{ items: SearchTag[] }>('/api/admin/search-tags');
  const produkttypen = useLoad<{ items: ProductTypeOption[] }>(
    '/api/admin/master/product_type',
  );
  const werkstoffe = useLoad<{ items: MaterialOption[] }>('/api/admin/master/material');
  const [err, setErr] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [bearbeite, setBearbeite] = useState<SearchTag | null>(null);

  const typOptionen = (produkttypen.data?.items ?? []).filter((t) => t.isActive);
  const werkstoffOptionen = (werkstoffe.data?.items ?? []).filter((w) => w.isActive);

  async function toggleActive(t: SearchTag): Promise<void> {
    try {
      await api.patch(`/api/admin/search-tags/${t.id}`, { isActive: !t.isActive });
      tags.reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  async function toggleQuickFilter(t: SearchTag): Promise<void> {
    try {
      await api.patch(`/api/admin/search-tags/${t.id}`, {
        showAsQuickFilter: !t.showAsQuickFilter,
      });
      tags.reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  async function remove(t: SearchTag): Promise<void> {
    if (!window.confirm(`Such-Tag "${t.displayName}" (#${t.key}) endgueltig loeschen?`)) {
      return;
    }
    try {
      await api.delete(`/api/admin/search-tags/${t.id}`);
      tags.reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  const sortiert = [...(tags.data?.items ?? [])].sort(
    (a, b) => a.quickFilterSortOrder - b.quickFilterSortOrder,
  );

  return (
    <>
      <PageHeader
        title="Suchbegriffe"
        lead="Tags und Synonyme ergaenzen ausschliesslich die Produktsuche. Sie behaupten
          keine technische Eignung -- Artikelnummer, Werkstoff, DN, PN und Norm bleiben
          die massgeblichen Produktdaten. Produkttyp und Werkstoff schraenken jeweils
          UND-verknuepft ein; leer heisst auf dieser Ebene 'alle zulassen'. Ohne jede
          Auswahl auf beiden Ebenen findet ein Tag bewusst nichts -- kein Freifahrschein
          fuer den ganzen Katalog."
        actions={
          <button type="button" className="primary" onClick={() => setNeuOffen(true)}>
            Tag anlegen
          </button>
        }
      />
      <Alert text={tags.error ?? produkttypen.error ?? werkstoffe.error ?? err} />
      {neuOffen && (
        <TagFormular
          typOptionen={typOptionen}
          werkstoffOptionen={werkstoffOptionen}
          onDone={() => {
            setNeuOffen(false);
            tags.reload();
          }}
          onCancel={() => setNeuOffen(false)}
        />
      )}
      {bearbeite && (
        <TagFormular
          bestehend={bearbeite}
          typOptionen={typOptionen}
          werkstoffOptionen={werkstoffOptionen}
          onDone={() => {
            setBearbeite(null);
            tags.reload();
          }}
          onCancel={() => setBearbeite(null)}
        />
      )}
      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Schluessel</th>
                <th>Anzeigename</th>
                <th>Produktfamilien</th>
                <th>Werkstoffe</th>
                <th className="num">Synonyme</th>
                <th>Status</th>
                <th>Schnellfilter</th>
                <th className="num">Reihenfolge</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortiert.map((t) => (
                <tr key={t.id}>
                  <td>
                    <code>{t.key}</code>
                  </td>
                  <td>{t.displayName}</td>
                  <td>
                    {t.productTypes.length === 0 ? (
                      <span className="muted">alle Produkttypen</span>
                    ) : (
                      t.productTypes.map((p) => p.code).join(', ')
                    )}
                  </td>
                  <td>
                    {t.materials.length === 0 ? (
                      <span className="muted">alle Werkstoffe</span>
                    ) : (
                      t.materials.map((m) => m.code).join(', ')
                    )}
                  </td>
                  <td className="num">{t.synonyms.length}</td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      onClick={() => void toggleActive(t)}
                    >
                      <StatusPill active={t.isActive} />
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      onClick={() => void toggleQuickFilter(t)}
                    >
                      <StatusPill
                        active={t.showAsQuickFilter}
                        activeText="als Hashtag"
                        inactiveText="nur Freitext"
                      />
                    </button>
                  </td>
                  <td className="num">{t.quickFilterSortOrder}</td>
                  <td className="row">
                    <button
                      type="button"
                      className="link"
                      onClick={() => setBearbeite(t)}
                    >
                      bearbeiten
                    </button>
                    <button type="button" className="link" onClick={() => void remove(t)}>
                      loeschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortiert.length === 0 && <p className="note">Noch keine Such-Tags angelegt.</p>}
      </div>
    </>
  );
}

function TagFormular({
  bestehend,
  typOptionen,
  werkstoffOptionen,
  onDone,
  onCancel,
}: {
  bestehend?: SearchTag;
  typOptionen: ProductTypeOption[];
  werkstoffOptionen: MaterialOption[];
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [f, setF] = useState<SearchTagForm>({
    key: bestehend?.key ?? '',
    displayName: bestehend?.displayName ?? '',
    description: bestehend?.description ?? '',
    isActive: bestehend?.isActive ?? true,
    showAsQuickFilter: bestehend?.showAsQuickFilter ?? false,
    quickFilterSortOrder: bestehend?.quickFilterSortOrder ?? 0,
    synonyms: bestehend?.synonyms ?? [],
    productTypeValueIds: bestehend?.productTypes.map((p) => p.id) ?? [],
    materialValueIds: bestehend?.materials.map((m) => m.id) ?? [],
  });
  const [neuesSynonym, setNeuesSynonym] = useState('');
  const [err, setErr] = useState<string | null>(null);

  function synonymHinzufuegen(): void {
    const wert = neuesSynonym.trim();
    if (wert === '' || f.synonyms.includes(wert)) return;
    setF({ ...f, synonyms: [...f.synonyms, wert] });
    setNeuesSynonym('');
  }

  function synonymEntfernen(wert: string): void {
    setF({ ...f, synonyms: f.synonyms.filter((s) => s !== wert) });
  }

  function typUmschalten(id: string): void {
    setF({
      ...f,
      productTypeValueIds: f.productTypeValueIds.includes(id)
        ? f.productTypeValueIds.filter((x) => x !== id)
        : [...f.productTypeValueIds, id],
    });
  }

  function werkstoffUmschalten(id: string): void {
    setF({
      ...f,
      materialValueIds: f.materialValueIds.includes(id)
        ? f.materialValueIds.filter((x) => x !== id)
        : [...f.materialValueIds, id],
    });
  }

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    const body = {
      ...(bestehend === undefined ? { key: f.key.trim() } : {}),
      displayName: f.displayName.trim(),
      description: f.description.trim(),
      isActive: f.isActive,
      showAsQuickFilter: f.showAsQuickFilter,
      quickFilterSortOrder: f.quickFilterSortOrder,
      synonyms: f.synonyms,
      productTypeValueIds: f.productTypeValueIds,
      materialValueIds: f.materialValueIds,
    };
    try {
      if (bestehend === undefined) {
        await api.post('/api/admin/search-tags', body);
      } else {
        await api.patch(`/api/admin/search-tags/${bestehend.id}`, body);
      }
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <section className="card highlight">
      <h3>
        {bestehend === undefined
          ? 'Tag anlegen'
          : `Tag "${bestehend.displayName}" bearbeiten`}
      </h3>
      <form onSubmit={speichern} className="grid2">
        <Field
          label="Schluessel"
          hint="Nur Kleinbuchstaben, Ziffern, Unterstrich. Nach dem Anlegen nicht mehr aenderbar."
        >
          <input
            required
            pattern="[a-z0-9_]+"
            value={f.key}
            disabled={bestehend !== undefined}
            onChange={(e) => setF({ ...f, key: e.target.value })}
          />
        </Field>
        <Field label="Anzeigename">
          <input
            required
            value={f.displayName}
            onChange={(e) => setF({ ...f, displayName: e.target.value })}
          />
        </Field>
        <Field label="Beschreibung" hint="Optional, fuer den Admin-Ueberblick.">
          <input
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
        <Field label="Reihenfolge Schnellfilter">
          <input
            type="number"
            value={f.quickFilterSortOrder}
            onChange={(e) =>
              setF({ ...f, quickFilterSortOrder: Number(e.target.value) || 0 })
            }
          />
        </Field>
        <label className="field">
          <span className="field-label">Status</span>
          <span className="row">
            <input
              type="checkbox"
              checked={f.isActive}
              onChange={(e) => setF({ ...f, isActive: e.target.checked })}
            />
            aktiv
          </span>
        </label>
        <label className="field">
          <span className="field-label">Schnellfilter</span>
          <span className="row">
            <input
              type="checkbox"
              checked={f.showAsQuickFilter}
              onChange={(e) => setF({ ...f, showAsQuickFilter: e.target.checked })}
            />
            als Hashtag-Button anzeigen
          </span>
        </label>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Synonyme</span>
          <div className="chip-list">
            {f.synonyms.map((s) => (
              <span key={s} className="chip">
                {s}
                <button
                  type="button"
                  onClick={() => synonymEntfernen(s)}
                  aria-label={`${s} entfernen`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="row">
            <input
              placeholder="Synonym, z. B. Viton oder low torque"
              value={neuesSynonym}
              onChange={(e) => setNeuesSynonym(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  synonymHinzufuegen();
                }
              }}
            />
            <button type="button" onClick={synonymHinzufuegen}>
              Hinzufuegen
            </button>
          </div>
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Produktfamilien (Produkttypen)</span>
          <div className="chip-list">
            {typOptionen.map((t) => (
              <label key={t.id} className="row" style={{ alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={f.productTypeValueIds.includes(t.id)}
                  onChange={() => typUmschalten(t.id)}
                />
                {t.code} – {t.label?.de}
              </label>
            ))}
          </div>
          <span className="note">Leer: alle Produkttypen zulassen.</span>
        </div>
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Werkstoffe</span>
          <div className="chip-list">
            {werkstoffOptionen.map((w) => (
              <label key={w.id} className="row" style={{ alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={f.materialValueIds.includes(w.id)}
                  onChange={() => werkstoffUmschalten(w.id)}
                />
                {w.code} – {w.label?.de}
              </label>
            ))}
          </div>
          <span className="note">
            Leer: alle Werkstoffe zulassen. Produkttyp und Werkstoff werden UND-
            verknuepft; ohne jede Auswahl auf beiden Ebenen findet der Tag nichts.
          </span>
        </div>
        <div className="row" style={{ alignSelf: 'end' }}>
          <button type="submit" className="primary">
            Speichern
          </button>
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </form>
      <Alert text={err} />
    </section>
  );
}
